/**
 * Unicorn stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const unicornStickersPack: ToilePack = {
    slug: 'unicorn-stickers',
    category: 'sticker',
    name: 'Unicorn stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'head', label: 'Unicorn head', subject: 'a unicorn head with a spiral horn and a rainbow mane'},
        {key: 'sleepy', label: 'Sleepy unicorn', subject: 'a round unicorn curled up asleep'},
        {key: 'cloud', label: 'Unicorn on a cloud', subject: 'a unicorn standing on a small cloud with stars'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'vinyl', label: 'Vinyl', style: 'glossy vinyl sticker, clean vector shapes, bold flat colour, a small highlight', palette: 'Bright flat colour with a white highlight', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
    ],
    directions: ['a baby unicorn instead', 'a whole sticker sheet', 'in pastel colors'],
    directionHint: 'try: a baby unicorn instead',
    seo: {
        title: 'Unicorn Stickers: Head, Sleepy, on a Cloud',
        metaDescription: 'Nine unicorn stickers — a rainbow-maned head, a sleepy curled-up unicorn, a unicorn on a cloud — in holographic, kawaii, and vinyl styles.',
        h1: 'Unicorn stickers: unicorn head, sleepy unicorn, on a cloud',
        tagline: 'Nine stickers, three unicorn moments, three styles each.',
        intro: 'A unicorn head with a spiral horn and rainbow mane, a round unicorn curled up asleep, and a unicorn standing on a small cloud with stars — three unicorn stickers, die-cut and ready to print. Each rendered holographic, kawaii, and glossy vinyl. Ask for a different mane colour, a rainbow-trail scene, or a whole herd composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            "a kids' bedroom wall",
            'a planner or journal page',
            'a birthday party favour',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine unicorn stickers — a unicorn head, a sleepy unicorn, and a unicorn on a cloud — in holographic, kawaii, and glossy vinyl finishes.'},
            {q: 'Can I get a different mane colour or scene?', a: 'Yes — the direction field takes any variation, including a different mane colour, a rainbow-trail scene, or a whole herd composition.'},
            {q: 'What does the holographic finish print as?', a: 'The holographic design is an illustrated iridescent sheen — print it on holographic vinyl stock for the real shifting effect, or on plain matte stock for a flat colourful version.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
        ],
    },
}
